import type { Unsubscribe } from 'firebase/firestore'
import { AuthenticationService } from '@core/services/authentication/authentication-service'
import {
  createFavourite,
  deleteFavourite,
  fetchFavourite,
  fetchFavourites,
  observeFavourite,
  observeFavourites,
} from '@core/services/user-favourites/user-favourites-service'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import {
  USER_FAVOURITE_TYPE_MICHAEL_OKUN_INSTAGRAM,
  USER_FAVOURITE_TYPE_NEWS,
  USER_FAVOURITE_TYPE_BAS_BLOEM_PODCAST,
  USER_FAVOURITE_TYPE_WEBINAR,
  type UserFavourite,
  type UserFavouriteType,
} from '@domain/models/user-favourite'

export const NEWS_FAVOURITE_TYPE = USER_FAVOURITE_TYPE_NEWS
export const PODCAST_FAVOURITE_TYPE = USER_FAVOURITE_TYPE_BAS_BLOEM_PODCAST
export const WEBINAR_FAVOURITE_TYPE = USER_FAVOURITE_TYPE_WEBINAR
export const INSTAGRAM_REEL_FAVOURITE_TYPE = USER_FAVOURITE_TYPE_MICHAEL_OKUN_INSTAGRAM

export class UserFavouritesFeature {
  private static getAuthUserIdOrThrow(): string {
    const userId = AuthenticationService.getInstance().userId
    if (!userId) {
      throw new CoreError({
        cause: CoreErrorCause.Forbidden,
        context: 'No authenticated user available for user favourites feature.',
      })
    }
    return userId
  }

  public static async getFavouriteItems(type?: UserFavouriteType): Promise<UserFavourite[]> {
    try {
      const userId = UserFavouritesFeature.getAuthUserIdOrThrow()
      return await fetchFavourites(userId, type)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch favourite items.',
        underlyingException: error,
      })
    }
  }

  public static async getFavouriteItemIds(type?: UserFavouriteType): Promise<string[]> {
    const favourites = await UserFavouritesFeature.getFavouriteItems(type)
    return favourites
      .map((item) => item.itemId)
      .filter((itemId) => itemId.trim().length > 0)
  }

  public static async getFavouriteItem(
    itemId: string,
    type?: UserFavouriteType,
  ): Promise<UserFavourite | null> {
    try {
      const userId = UserFavouritesFeature.getAuthUserIdOrThrow()
      return await fetchFavourite(userId, itemId, type)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch favourite item.',
        underlyingException: error,
      })
    }
  }

  public static observeFavouriteItems(
    callback: (items: UserFavourite[]) => void,
    onError?: (error: Error) => void,
    type?: UserFavouriteType,
  ): Unsubscribe {
    try {
      const userId = UserFavouritesFeature.getAuthUserIdOrThrow()
      return observeFavourites(
        userId,
        callback,
        onError,
        type,
      )
    } catch (error) {
      onError?.(
        error instanceof CoreError
          ? error
          : new CoreError({
            cause: CoreErrorCause.Unknown,
            context: 'Failed to observe favourite items.',
            underlyingException: error,
          }),
      )
      return () => {}
    }
  }

  public static observeFavouriteItem(
    itemId: string,
    callback: (favourite: UserFavourite | null) => void,
    onError?: (error: Error) => void,
    type?: UserFavouriteType,
  ): Unsubscribe {
    try {
      const userId = UserFavouritesFeature.getAuthUserIdOrThrow()
      return observeFavourite(
        userId,
        itemId,
        callback,
        onError,
        type,
      )
    } catch (error) {
      onError?.(
        error instanceof CoreError
          ? error
          : new CoreError({
            cause: CoreErrorCause.Unknown,
            context: 'Failed to observe favourite item.',
            underlyingException: error,
          }),
      )
      return () => {}
    }
  }

  public static async addFavouriteItem(
    itemId: string,
    type: UserFavouriteType,
  ): Promise<string> {
    try {
      const userId = UserFavouritesFeature.getAuthUserIdOrThrow()
      return await createFavourite({
        userId,
        itemId,
        type,
      })
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to add favourite item.',
        underlyingException: error,
      })
    }
  }

  public static async removeFavouriteItem(
    itemId: string,
    type: UserFavouriteType,
  ): Promise<void> {
    try {
      const userId = UserFavouritesFeature.getAuthUserIdOrThrow()
      const favourites = await fetchFavourites(userId, type)
      const favouritesToDelete = favourites.filter((item) => item.itemId === itemId)
      await Promise.all(
        favouritesToDelete.map((item) => deleteFavourite(item.uid)),
      )
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to remove favourite item.',
        underlyingException: error,
      })
    }
  }

  public static async toggleFavouriteItem(
    itemId: string,
    type: UserFavouriteType,
  ): Promise<boolean> {
    try {
      const userId = UserFavouritesFeature.getAuthUserIdOrThrow()
      const existingFavourite = await fetchFavourite(
        userId,
        itemId,
        type,
      )

      if (existingFavourite) {
        await UserFavouritesFeature.removeFavouriteItem(itemId, type)
        return false
      }

      await createFavourite({
        userId,
        itemId,
        type,
      })
      return true
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to toggle favourite item.',
        underlyingException: error,
      })
    }
  }

  public static async getFavouriteNewsItems(): Promise<UserFavourite[]> {
    return UserFavouritesFeature.getFavouriteItems(NEWS_FAVOURITE_TYPE)
  }

  public static async getFavouriteNewsIds(): Promise<string[]> {
    return UserFavouritesFeature.getFavouriteItemIds(NEWS_FAVOURITE_TYPE)
  }

  public static async getFavouriteNewsItem(
    articleId: string,
  ): Promise<UserFavourite | null> {
    return UserFavouritesFeature.getFavouriteItem(articleId, NEWS_FAVOURITE_TYPE)
  }

  public static observeFavouriteNewsIds(
    callback: (ids: string[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    return UserFavouritesFeature.observeFavouriteItems(
      (items) => {
        callback(
          items
            .map((item) => item.itemId)
            .filter((itemId) => itemId.trim().length > 0),
        )
      },
      onError,
      NEWS_FAVOURITE_TYPE,
    )
  }

  public static observeFavouriteNewsItem(
    articleId: string,
    callback: (favourite: UserFavourite | null) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    return UserFavouritesFeature.observeFavouriteItem(
      articleId,
      callback,
      onError,
      NEWS_FAVOURITE_TYPE,
    )
  }

  public static async addFavouriteNewsItem(articleId: string): Promise<string> {
    return UserFavouritesFeature.addFavouriteItem(articleId, NEWS_FAVOURITE_TYPE)
  }

  public static async removeFavouriteNewsItem(articleId: string): Promise<void> {
    return UserFavouritesFeature.removeFavouriteItem(articleId, NEWS_FAVOURITE_TYPE)
  }

  public static async toggleFavouriteNewsItem(articleId: string): Promise<boolean> {
    return UserFavouritesFeature.toggleFavouriteItem(articleId, NEWS_FAVOURITE_TYPE)
  }
}

export async function getFavouriteNewsItems(): Promise<UserFavourite[]> {
  return UserFavouritesFeature.getFavouriteNewsItems()
}

export async function getFavouriteNewsIds(): Promise<string[]> {
  return UserFavouritesFeature.getFavouriteNewsIds()
}

export async function getFavouriteNewsItem(
  articleId: string,
): Promise<UserFavourite | null> {
  return UserFavouritesFeature.getFavouriteNewsItem(articleId)
}

export function observeFavouriteNewsIds(
  callback: (ids: string[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return UserFavouritesFeature.observeFavouriteNewsIds(callback, onError)
}

export function observeFavouriteNewsItem(
  articleId: string,
  callback: (favourite: UserFavourite | null) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return UserFavouritesFeature.observeFavouriteNewsItem(articleId, callback, onError)
}

export async function addFavouriteNewsItem(articleId: string): Promise<string> {
  return UserFavouritesFeature.addFavouriteNewsItem(articleId)
}

export async function removeFavouriteNewsItem(articleId: string): Promise<void> {
  return UserFavouritesFeature.removeFavouriteNewsItem(articleId)
}

export async function toggleFavouriteNewsItem(
  articleId: string,
): Promise<boolean> {
  return UserFavouritesFeature.toggleFavouriteNewsItem(articleId)
}

export async function getFavouriteItems(type?: UserFavouriteType): Promise<UserFavourite[]> {
  return UserFavouritesFeature.getFavouriteItems(type)
}

export async function getFavouriteItemIds(type?: UserFavouriteType): Promise<string[]> {
  return UserFavouritesFeature.getFavouriteItemIds(type)
}

export async function getFavouriteItem(
  itemId: string,
  type?: UserFavouriteType,
): Promise<UserFavourite | null> {
  return UserFavouritesFeature.getFavouriteItem(itemId, type)
}

export function observeFavouriteItems(
  callback: (items: UserFavourite[]) => void,
  onError?: (error: Error) => void,
  type?: UserFavouriteType,
): Unsubscribe {
  return UserFavouritesFeature.observeFavouriteItems(callback, onError, type)
}

export function observeFavouriteItem(
  itemId: string,
  callback: (favourite: UserFavourite | null) => void,
  onError?: (error: Error) => void,
  type?: UserFavouriteType,
): Unsubscribe {
  return UserFavouritesFeature.observeFavouriteItem(itemId, callback, onError, type)
}

export async function addFavouriteItem(
  itemId: string,
  type: UserFavouriteType,
): Promise<string> {
  return UserFavouritesFeature.addFavouriteItem(itemId, type)
}

export async function removeFavouriteItem(
  itemId: string,
  type: UserFavouriteType,
): Promise<void> {
  return UserFavouritesFeature.removeFavouriteItem(itemId, type)
}

export async function toggleFavouriteItem(
  itemId: string,
  type: UserFavouriteType,
): Promise<boolean> {
  return UserFavouritesFeature.toggleFavouriteItem(itemId, type)
}
